import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/stores/appStore';
import { captureApi, mediaApi, type CaptureResult } from '@/lib/api';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { X, Link2, Loader2, Download, Sparkles } from 'lucide-react';

interface CaptureModalProps {
  onClose: () => void;
  /** Called with the captured content — opens the composer prefilled. */
  onUse: (args: { mediaKeys: string[]; content: string }) => void;
}

/**
 * Content Capture: paste a link to a post on X / Instagram / Facebook / TikTok,
 * pull its media into the library, and send caption + media into the composer.
 */
export function CaptureModal({ onClose, onUse }: CaptureModalProps) {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const [url, setUrl] = useState('');

  const capture = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return captureApi.capture(token!, currentWorkspace!.id, url.trim());
    },
  });

  const result: CaptureResult | undefined = capture.data?.capture;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Download className="h-4 w-4 text-accent" />
            Content Capture
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-muted-foreground">
            Paste a link to a post on X, Instagram, Facebook, or TikTok. Enflux grabs the media and
            caption so you can repost it on your channels.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 bg-input border border-border rounded-md px-3 py-2">
              <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && url.trim() && !capture.isPending) capture.mutate();
                }}
                placeholder="https://www.instagram.com/reel/…"
                className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
            <button
              onClick={() => capture.mutate()}
              disabled={!url.trim() || capture.isPending}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors"
            >
              {capture.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Capture
            </button>
          </div>

          {capture.error && (
            <p className="text-xs text-destructive">
              {(capture.error as Error).message || 'Capture failed — check the link and try again.'}
            </p>
          )}

          {result && (
            <div className="space-y-3 border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 uppercase tracking-wide font-semibold text-accent">
                  <PlatformIcon platform={result.platform} size="xs" variant="glyph" />
                  {result.platform}
                </span>
                {result.author && <span>by @{result.author.replace(/^@/, '')}</span>}
                <span className="ml-auto">{result.media.length} media saved to library</span>
              </div>
              {result.media.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {result.media.map((m) =>
                    m.is_video ? (
                      <video
                        key={m.id}
                        src={mediaApi.serveUrl(m.r2_key)}
                        controls
                        className="h-28 rounded-md border border-border bg-black"
                      />
                    ) : (
                      <img
                        key={m.id}
                        src={mediaApi.serveUrl(m.r2_key)}
                        alt="Captured media"
                        className="h-28 rounded-md border border-border object-cover"
                      />
                    )
                  )}
                </div>
              )}
              {result.caption && (
                <p className="text-xs text-foreground/90 whitespace-pre-wrap line-clamp-6">
                  {result.caption}
                </p>
              )}
              <button
                onClick={() =>
                  onUse({
                    mediaKeys: result.media.map((m) => m.r2_key),
                    content: result.caption,
                  })
                }
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Use in post
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
